// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import jettingAppLoadProduct from "@stoneage/App/Handlers/JettingApp/jettingAppLoadProduct";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@stoneage/MobiusOverrides/LazyImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@stoneage/Overrides/Components/ProductPartNumbers";
import * as React from "react";
import { FC } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    productPartNumber?: string;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    isLoadingProduct: state.xJettingAppCalculators.isLoadingProduct,
    product: state.xJettingAppCalculators.product?.[ownProps?.productPartNumber || ""],
});
const mapDispatchToProps = {
    jettingAppLoadProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface JettingAppProductCardStyles {
    wrapper?: InjectableCss;
    link?: LinkPresentationProps;
    leftPart?: InjectableCss;
    rightPart?: InjectableCss;
    imageAndDescription?: InjectableCss;
    image?: LazyImageProps;
    partNumber?: SmallHeadingAndTextStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    productTitle?: TypographyPresentationProps;
}

export const jettingAppProductCardStyles: JettingAppProductCardStyles = {};

const styles = jettingAppProductCardStyles;

const JettingAppProductCard: FC<Props> = ({ product, productPartNumber, isLoadingProduct }) => {
    if (isLoadingProduct) {
        return <LoadingSpinner />;
    }

    if (!product || !productPartNumber) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.imageAndDescription}>
                <StyledWrapper {...styles.leftPart}>
                    <LazyImage src={product?.smallImagePath} altText={product.imageAltText} {...styles.image} />
                </StyledWrapper>
                <StyledWrapper {...styles.rightPart}>
                    <Typography {...styles.productTitle}>{product.productTitle}</Typography>

                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        showManufacturerItem={false}
                        extendedStyles={styles.productPartNumbers}
                    />
                    <Link href={product.canonicalUrl} {...styles.link}>
                        {translate("Return to product")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(JettingAppProductCard);
